import React, { useMemo, useState } from "react";
import {
	Box, Typography, TextField, Chip, Button, Dialog,
	DialogTitle, DialogContent, DialogActions,
} from "@mui/material";
import { ChatBubbleOutline, EmailOutlined } from "@mui/icons-material";
import { Helmet } from "react-helmet";
import api from "../../../services/Axios";
import AdminPageHeader, { PageSpinner } from "../../../components/AdminPageHeader";
import { patchAdminData, useAdminData } from "../../../services/adminDataStore";

const CARD = {
	backgroundColor: "#FFFFFF",
	borderRadius: "20px",
	border: "1px solid #EEF0F5",
	boxShadow: "0 2px 8px rgba(17,19,26,0.04)",
};

const MODE_COLORS = { chat: "#60a5fa", voice: "#4ade80", video: "#fbbf24" };

const inputSx = {
	"& .MuiOutlinedInput-root": {
		color: "#1F2430",
		backgroundColor: "#F7F8FC",
		fontSize: 13,
		borderRadius: "12px",
	},
	"& .MuiOutlinedInput-notchedOutline": { borderColor: "#E2E4EE" },
};

function ModeBadge({ mode }) {
	if (!mode) return null;
	return (
		<Chip
			label={mode}
			size="small"
			sx={{
				fontSize: 10,
				height: 20,
				backgroundColor: `${MODE_COLORS[mode] || "#9CA3AF"}22`,
				color: MODE_COLORS[mode] || "#9CA3AF",
				textTransform: "capitalize",
			}}
		/>
	);
}

function safetySummary(safety) {
	if (!safety || typeof safety !== "object") return null;
	const triggered = safety.triggered || safety.categories || {};
	const keys = Object.keys(triggered).filter((k) => triggered[k]);
	if (!keys.length && safety.mapped_category) return safety.mapped_category;
	return keys.slice(0, 4).join(", ") || null;
}

function BanConfirmDialog({ user, onCancel, onConfirm }) {
	const [reason, setReason] = useState("");
	const [submitting, setSubmitting] = useState(false);

	const handleConfirm = async () => {
		setSubmitting(true);
		try {
			await onConfirm(reason.trim());
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<Dialog
			open
			onClose={onCancel}
			PaperProps={{
				sx: {
					backgroundColor: "#FFFFFF",
					minWidth: 360,
					borderRadius: "20px",
					boxShadow: "0 20px 60px rgba(17,19,26,0.18)",
				},
			}}
		>
			<DialogTitle sx={{ color: "#12131A", fontSize: 18, fontWeight: 700 }}>
				Ban {user.name || user.user_id}?
			</DialogTitle>
			<DialogContent>
				<Typography fontSize={12} color="#6B7280" mb={1.5}>
					This Clerk-bans the user and sets status to suspended. Add an optional reason for the record.
				</Typography>
				<TextField
					fullWidth
					size="small"
					multiline
					minRows={2}
					placeholder="Reason (optional)"
					value={reason}
					onChange={(e) => setReason(e.target.value)}
					sx={inputSx}
				/>
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 2 }}>
				<Button onClick={onCancel} sx={{ color: "#6B7280", fontSize: 12, textTransform: "none" }}>
					Cancel
				</Button>
				<Button
					onClick={handleConfirm}
					disabled={submitting}
					sx={{
						color: "#DC2626",
						border: "1px solid #FCA5A5",
						backgroundColor: "#FEF2F2",
						fontSize: 12,
						borderRadius: "10px",
						textTransform: "none",
						fontWeight: 600,
					}}
				>
					{submitting ? "Banning..." : "Confirm ban"}
				</Button>
			</DialogActions>
		</Dialog>
	);
}

export default function Moderation() {
	const { softBanned, loaded } = useAdminData();
	const users = softBanned || [];
	const [search, setSearch] = useState("");
	const [actionLoadingId, setActionLoadingId] = useState(null);
	const [confirmUser, setConfirmUser] = useState(null);
	const [expandedId, setExpandedId] = useState(null);
	const [flaggedByUser, setFlaggedByUser] = useState({});
	const [flaggedLoadingId, setFlaggedLoadingId] = useState(null);
	const [error, setError] = useState(null);

	const removeUser = (userId) => {
		patchAdminData((prev) => ({
			...prev,
			softBanned: (prev.softBanned || []).filter((u) => u.user_id !== userId),
		}));
	};

	const filtered = useMemo(() => {
		if (!search.trim()) return users;
		const q = search.toLowerCase();
		return users.filter((u) =>
			(u.name || "").toLowerCase().includes(q) ||
			(u.email || "").toLowerCase().includes(q) ||
			(u.user_id || "").toLowerCase().includes(q) ||
			(u.status_reason || "").toLowerCase().includes(q)
		);
	}, [users, search]);

	const loadFlagged = async (userId) => {
		if (flaggedByUser[userId]) return;
		setFlaggedLoadingId(userId);
		try {
			const res = await api.get(`/admin/moderation/users/${userId}/flagged-messages`);
			setFlaggedByUser((prev) => ({ ...prev, [userId]: res.data?.messages || [] }));
		} catch (e) {
			console.error(e);
			setFlaggedByUser((prev) => ({ ...prev, [userId]: [] }));
		} finally {
			setFlaggedLoadingId(null);
		}
	};

	const toggleExpand = async (userId) => {
		if (expandedId === userId) {
			setExpandedId(null);
			return;
		}
		setExpandedId(userId);
		await loadFlagged(userId);
	};

	const handleIgnore = async (user) => {
		setActionLoadingId(user.user_id);
		try {
			await api.post(`/admin/moderation/users/${user.user_id}/ignore`);
			removeUser(user.user_id);
			if (expandedId === user.user_id) setExpandedId(null);
		} catch (e) {
			console.error(e);
			setError(e?.response?.data?.detail || "Ignore failed");
		} finally {
			setActionLoadingId(null);
		}
	};

	const handleBanConfirm = async (reason) => {
		if (!confirmUser) return;
		setActionLoadingId(confirmUser.user_id);
		try {
			await api.post(`/admin/moderation/users/${confirmUser.user_id}/ban`, { reason: reason || null });
			removeUser(confirmUser.user_id);
			if (expandedId === confirmUser.user_id) setExpandedId(null);
		} catch (e) {
			console.error(e);
			setError(e?.response?.data?.detail || "Ban failed");
		} finally {
			setActionLoadingId(null);
			setConfirmUser(null);
		}
	};

	const fmtDate = (iso) => {
		if (!iso) return "—";
		return new Date(iso).toLocaleString("en-US", {
			month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", hour12: false,
		});
	};

	return (
		<Box className="dashboard-main">
			<Helmet>
				<title>Moderation</title>
			</Helmet>
			<Box maxWidth={1600} mx="auto">
				<AdminPageHeader
					title="Moderation"
					subtitle={`${filtered.length} soft-banned user${filtered.length === 1 ? "" : "s"} awaiting Ban / Ignore`}
					actions={
						<TextField
							size="small"
							placeholder="Search name, email, id"
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							sx={{ ...inputSx, minWidth: { xs: "100%", sm: 220 } }}
						/>
					}
				/>
				{!loaded ? <PageSpinner /> : (
				<>
				{error && (
					<Typography fontSize={12} color="#fb7185" mb={1.5}>{error}</Typography>
				)}
				<Box p={{ xs: 1.5, sm: 2.5 }} sx={CARD}>
					<Box display="flex" flexDirection="column" gap={1.25}>
							{filtered.map((u) => {
								const open = expandedId === u.user_id;
								const msgs = flaggedByUser[u.user_id];
								return (
									<Box
										key={u.user_id}
										sx={{
											position: "relative",
											borderRadius: "16px",
											p: 2,
											overflow: "hidden",
											background: "#FFFFFF",
											boxShadow: "0 2px 10px rgba(17,19,26,0.05)",
											border: open ? "1px solid rgba(236,72,153,.28)" : "1px solid #EEF0F5",
											transition: "box-shadow .2s ease, border-color .2s ease",
											"&:hover": {
												boxShadow: "0 8px 20px rgba(168,85,247,0.10)",
												borderColor: "rgba(236,72,153,.25)",
											},
										}}
									>
										<Box
											display="flex"
											flexDirection={{ xs: "column", sm: "row" }}
											justifyContent="space-between"
											gap={1.5}
											sx={{ cursor: "pointer" }}
											onClick={() => toggleExpand(u.user_id)}
										>
											<Box flex={1} minWidth={0}>
												<Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={0.5}>
													<Typography fontSize={14} color="#1F2430" fontWeight={600}>
														{u.name || "(no name)"}
													</Typography>
													<Chip
														label="Soft-banned"
														size="small"
														sx={{
															fontSize: 10, height: 20,
															backgroundColor: "#FEF3C7",
															color: "#B45309",
															fontWeight: 600,
														}}
													/>
													{typeof u.flagged_count === "number" && (
														<Typography fontSize={11} color="#8B93A1">
															{u.flagged_count} flagged msg{u.flagged_count === 1 ? "" : "s"}
														</Typography>
													)}
												</Box>
												<Typography
													fontSize={13}
													fontWeight={500}
													sx={{
														color: "rgba(17,19,26,0.6)",
														mb: 1,
														display: "flex",
														alignItems: "center",
														gap: 0.75,
													}}
												>
													<EmailOutlined sx={{ fontSize: 15, color: "#A855F7" }} />
													{u.email || u.user_id}
												</Typography>
												{u.status_reason ? (
													<Typography fontSize={12} color="#6B7280" sx={{ wordBreak: "break-word" }}>
														{u.status_reason}
														{u.status_category ? ` · ${u.status_category}` : ""}
													</Typography>
												) : (
													<Typography
														fontSize={13}
														fontWeight={500}
														sx={{
															color: "rgba(17,19,26,0.62)",
															display: "flex",
															alignItems: "center",
															gap: 0.75,
														}}
													>
														<ChatBubbleOutline sx={{ fontSize: 16, color: "#EC4899" }} />
														Click to view flagged messages
													</Typography>
												)}
												{u.updated_at && (
													<Typography fontSize={11} color="#9CA3AF" mt={0.75}>
														Queued {fmtDate(u.updated_at)}
													</Typography>
												)}
											</Box>

											<Box
												display="flex"
												alignItems={{ xs: "stretch", sm: "center" }}
												gap={1}
												onClick={(e) => e.stopPropagation()}
											>
												<Button
													size="small"
													onClick={() => handleIgnore(u)}
													disabled={actionLoadingId === u.user_id}
													sx={{
														minWidth: 90,
														px: 2,
														py: 0.8,
														borderRadius: "14px",
														textTransform: "none",
														fontWeight: 600,
														fontSize: "12px",
														color: "#16A34A",
														background: "#F0FDF4",
														border: "1px solid rgba(22,163,74,.2)",
														"&:hover": { background: "#DCFCE7", color: "#15803D" },
														"&.Mui-disabled": { color: "#8B93A1", background: "#F1F2F6" },
													}}
												>
													{actionLoadingId === u.user_id ? "..." : "Ignore"}
												</Button>
												<Button
													size="small"
													onClick={() => setConfirmUser(u)}
													disabled={actionLoadingId === u.user_id}
													sx={{
														minWidth: 90,
														px: 2,
														py: 0.8,
														borderRadius: "14px",
														textTransform: "none",
														fontWeight: 600,
														fontSize: "12px",
														color: "#DC2626",
														background: "#FEF2F2",
														border: "1px solid rgba(220,38,38,.2)",
														"&:hover": { background: "#FEE2E2", color: "#B91C1C" },
														"&.Mui-disabled": { color: "#8B93A1", background: "#F1F2F6" },
													}}
												>
													Ban
												</Button>
											</Box>
										</Box>

										{open && (
											<Box mt={1.5} pt={1.5} borderTop="1px solid #EEF0F5">
												<Typography fontSize={11} color="#6B7280" mb={1} fontWeight={600}>
													Flagged messages
												</Typography>
												{flaggedLoadingId === u.user_id ? (
													<Typography fontSize={12} color="#9CA3AF">Loading…</Typography>
												) : !msgs || msgs.length === 0 ? (
													<Typography fontSize={12} color="#9CA3AF">No flagged messages found.</Typography>
												) : (
													<Box display="flex" flexDirection="column" gap={1} maxHeight={320} overflow="auto">
														{msgs.map((m) => (
															<Box key={m.message_id} sx={{ backgroundColor: "#F7F8FC", borderRadius: 1.5, p: 1.25 }}>
																<Box display="flex" gap={0.75} alignItems="center" mb={0.5} flexWrap="wrap">
																	<ModeBadge mode={m.mode} />
																	<Typography fontSize={10} color="#8B93A1">{fmtDate(m.created_at)}</Typography>
																	{safetySummary(m.safety) && (
																		<Typography fontSize={10} color="#d97706">
																			{safetySummary(m.safety)}
																		</Typography>
																	)}
																</Box>
																<Typography fontSize={12} color="#3A3F4B" sx={{ wordBreak: "break-word", whiteSpace: "pre-wrap" }}>
																	{m.content}
																</Typography>
															</Box>
														))}
													</Box>
												)}
											</Box>
										)}
									</Box>
								);
							})}
							{filtered.length === 0 && (
								<Box py={8} textAlign="center">
									<Typography fontSize={15} fontWeight={600} color="#1F2430">Queue is clear</Typography>
									<Typography fontSize={13} color="#9CA3AF" mt={0.75}>
										No soft-banned users awaiting review.
									</Typography>
								</Box>
							)}
						</Box>
				</Box>

				{confirmUser && (
					<BanConfirmDialog
						user={confirmUser}
						onCancel={() => setConfirmUser(null)}
						onConfirm={handleBanConfirm}
					/>
				)}
				</>
				)}
			</Box>
		</Box>
	);
}
